'use client';
import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import * as Dialog from '@radix-ui/react-dialog';
import { TestimonialsBlock, Testimonials } from '../types';
import NavigationArrow from '@/assets/nav_arrow.svg';
import clsx from 'clsx';

import 'swiper/css';
import 'swiper/css/pagination';
import ContentContainer from '../layout/ContentContainer';
import { SupportedLanguages } from '@/types/globals';

const text = {
  fr: {
    readMore: 'Lire la suite',
  },
  en: {
    readMore: 'Read more',
  },
};

// Modal component for full testimonial text using Radix Dialog
const TestimonialModal = ({
  isOpen,
  onClose,
  testimonial,
}: {
  isOpen: boolean;
  onClose: () => void;
  testimonial: Testimonials | null;
}) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-12 shadow-2xl">
            {/* Close button */}
            <Dialog.Close className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:cursor-pointer hover:bg-gray-200">
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Dialog.Close>

            {/* Modal content */}
            <div className="space-y-6">
              {/* Testimonial Title */}
              {testimonial?.testimonial_title && (
                <Dialog.Title className="font-source-serif-pro text-2xl text-indigo-800">
                  {testimonial.testimonial_title}
                </Dialog.Title>
              )}

              {/* Testimonial Text */}
              {testimonial?.testimonial_text && (
                <Dialog.Description className="text-graphite-900 font-source-sans-pro text-lg leading-relaxed">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: testimonial.testimonial_text,
                    }}
                  />
                </Dialog.Description>
              )}

              {/* Author Information */}
              <div className="flex items-center space-x-4 border-t border-gray-200 pt-4">
                <div className="flex flex-col">
                  {testimonial?.author_name && (
                    <span className="text-graphite-700 font-inter text-lg font-semibold">
                      {testimonial.author_name}
                    </span>
                  )}
                  {testimonial?.author_title && (
                    <span className="text-graphite-700 font-inter text-md">
                      {testimonial.author_title}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default function TestimonialBlock({
  block,
  locale,
}: {
  block: TestimonialsBlock;
  locale: SupportedLanguages;
}) {
  const swiperRef = useRef<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] =
    useState<Testimonials | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleOpenModal = (testimonial: Testimonials) => {
    setSelectedTestimonial(testimonial);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTestimonial(null);
  };

  return block.testimonials && block.testimonials.length > 0 ? (
    <ContentContainer>
      <div className="w-full items-center justify-center gap-6">
        <div className="relative mx-auto w-full overflow-visible">
          <Swiper
            modules={[Navigation, Pagination, A11y]}
            spaceBetween={24}
            slidesPerView={1}
            // Ensure symmetrical side padding on small screens to match ContentContainer (px-4)
            slidesOffsetBefore={16}
            slidesOffsetAfter={16}
            onSwiper={swiper => {
              swiperRef.current = swiper;
            }}
            onSlideChange={swiper => {
              setActiveIndex(swiper.activeIndex);
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            breakpoints={{
              0: {
                slidesPerView: 1.1,
                spaceBetween: 8,
                slidesOffsetBefore: 8,
                slidesOffsetAfter: 8,
              },
              320: {
                slidesPerView: 1.1,
                spaceBetween: 8,
                slidesOffsetBefore: 8,
                slidesOffsetAfter: 8,
              },
              768: {
                slidesPerView: 1.1,
                spaceBetween: 24,
                slidesOffsetBefore: 16,
                slidesOffsetAfter: 16,
              },
              1024: {
                slidesPerView: 2.1, // show a slight peek of the third card
                spaceBetween: 24,
                // ContentContainer removes horizontal padding from ~1160px up; approximate at 1024
                slidesOffsetBefore: 0,
                slidesOffsetAfter: 0,
              },
              1280: {
                slidesPerView: 2.2, // stronger peek on larger screens
                spaceBetween: 24,
              },
              1536: {
                slidesPerView: 2.3, // widest screens show more of the third card
                spaceBetween: 24,
              },
            }}
          >
            {block.testimonials.map(
              (testimonial: Testimonials, index: number) => (
                <SwiperSlide
                  key={`${testimonial.id}-${index}`}
                  className="h-full"
                >
                  <div
                    className={clsx(
                      'group hover:border-qf-purple/20 relative h-[400px] rounded-2xl border-2 border-[#CBC5D9]/20 bg-[#D9D3E8]/20 p-6 transition-all duration-300 sm:h-[400px] md:h-[420px] xl:h-[430px] 2xl:h-[440px]',
                      activeIndex === index || activeIndex + 1 === index
                        ? 'opacity-100'
                        : 'opacity-60',
                    )}
                  >
                    {/* Testimonial Content */}
                    <div className="flex h-full flex-col space-y-4">
                      {/* Testimonial Title */}
                      {testimonial.testimonial_title && (
                        <h3 className="font-source-serif-pro text-xl text-indigo-800">
                          {testimonial.testimonial_title}
                        </h3>
                      )}

                      {/* Testimonial Text */}
                      {testimonial.testimonial_text && (
                        <div className="text-graphite-900 font-source-sans-pro text-lg">
                          <div className="line-clamp-3 sm:line-clamp-4 md:line-clamp-5">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: testimonial.testimonial_text,
                              }}
                            />
                          </div>
                          <button
                            onClick={() => handleOpenModal(testimonial)}
                            className="text-qf-purple font-source-sans-pro mt-2 text-lg font-semibold hover:underline"
                          >
                            {text[locale].readMore}
                          </button>
                        </div>
                      )}

                      {/* Author Information */}
                      <div className="mt-auto flex items-center space-x-4">
                        {/* Author Details */}
                        <div className="flex flex-col">
                          {testimonial.author_name && (
                            <span className="text-graphite-700 font-inter text-md font-semibold">
                              {testimonial.author_name}
                            </span>
                          )}
                          {testimonial.author_title && (
                            <span className="text-graphite-700 font-inter text-md">
                              {testimonial.author_title}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ),
            )}
          </Swiper>
        </div>

        {block.testimonials.length > 1 && (
          <div className="mt-6 flex items-center justify-center gap-4">
            <div className="flex gap-1 rounded-full border border-gray-200">
              <button
                className={clsx(
                  'group bg-qf-purple/10 hover:bg-qf-purple m-1 flex h-10 w-10 items-center justify-center rounded-full transition-opacity hover:cursor-pointer hover:bg-gray-100',
                  isBeginning ? 'opacity-30' : 'opacity-100',
                )}
                onClick={() => swiperRef.current?.slidePrev()}
              >
                <NavigationArrow className="text-qf-purple group-hover:text-white" />
              </button>
              <button
                className={clsx(
                  'group bg-qf-purple/10 hover:bg-qf-purple m-1 flex h-10 w-10 items-center justify-center rounded-full transition-opacity hover:cursor-pointer hover:bg-gray-100',
                  isEnd ? 'opacity-30' : 'opacity-100',
                )}
                onClick={() => swiperRef.current?.slideNext()}
              >
                <NavigationArrow className="text-qf-purple rotate-180 group-hover:text-white" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      <TestimonialModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        testimonial={selectedTestimonial}
      />
    </ContentContainer>
  ) : null;
}
